/**
 * Classic main-screen hero media: image, muted video, or muted YouTube embed.
 * Primary from banner `url` when set; on load failure falls back to `attachment`.
 *
 * Direct file video prefers `react-native-video` when the native `RCTVideo` view is linked;
 * otherwise uses a muted HTML5 `<video>` in WebView so a stale binary does not crash.
 *
 * `loop` (from app_config `banner_loop`): true = indefinite loop; false = play once
 * (stays on last frame; does not remount on re-focus).
 *
 * Once loaded, image/video stay mounted across Journey / Consultation / Menu / Plans /
 * Order navigations — pause while blurred, resume when focused. No unload/reload.
 *
 * Hero mounts under the bootstrap splash. `onReady` fires after a visible banner
 * still (image / poster) or first video frame is committed (two rAFs).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, StyleSheet, UIManager, View, type StyleProp, type ViewStyle } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { WebView, type WebViewMessageEvent } from 'react-native-webview';
import type { BannerHeroMedia, BannerMediaKind } from '../data/domains/config';

export type MainScreenClassicBannerMediaProps = {
  media: BannerHeroMedia;
  width: number;
  height: number;
  style?: StyleProp<ViewStyle>;
  /** When true, video/YouTube loops. When false, plays once and holds last frame. Default true. */
  loop?: boolean;
  /** Fired once when hero is painted and ready for splash fade (image or video). */
  onReady?: () => void;
};

/** Wait until after the next two frames so the hero is composited under the opaque splash. */
function afterPaint(cb: () => void): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(cb);
  });
}

type ActiveMedia = {
  kind: BannerMediaKind;
  uri: string;
  youtube_id: string | null;
};

/** True when the native binary registered `RCTVideo` (requires rebuild after adding react-native-video). */
function isNativeVideoAvailable(): boolean {
  try {
    const ui = UIManager as {
      getViewManagerConfig?: (name: string) => unknown;
      hasViewManagerConfig?: (name: string) => boolean;
    };
    if (typeof ui.getViewManagerConfig === 'function') {
      return ui.getViewManagerConfig('RCTVideo') != null;
    }
    if (typeof ui.hasViewManagerConfig === 'function') {
      return ui.hasViewManagerConfig('RCTVideo');
    }
  } catch {
    return false;
  }
  return false;
}

function youtubeEmbedHtml(video_id: string, loop: boolean): string {
  const loop_qs = loop ? `&loop=1&playlist=${video_id}` : '';
  const src =
    `https://www.youtube.com/embed/${video_id}` +
    `?autoplay=1&mute=1${loop_qs}` +
    `&controls=0&playsinline=1&rel=0&modestbranding=1&fs=0&iv_load_policy=3`;
  return (
    `<!DOCTYPE html><html><head>` +
    `<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />` +
    `<style>` +
    `html,body{margin:0;padding:0;width:100%;height:100%;background:#000;overflow:hidden}` +
    `.wrap{position:absolute;inset:0;overflow:hidden}` +
    `iframe{position:absolute;top:50%;left:50%;width:100vw;height:56.25vw;min-height:100%;min-width:177.78vh;` +
    `transform:translate(-50%,-50%);border:0;pointer-events:none}` +
    `</style></head><body><div class="wrap">` +
    `<iframe id="yt" src="${src}" allow="autoplay; encrypted-media" allowfullscreen></iframe>` +
    `</div>` +
    `<script>` +
    `setTimeout(function(){try{window.ReactNativeWebView.postMessage('ready')}catch(e){}},900);` +
    `</script>` +
    `</body></html>`
  );
}

function fileVideoEmbedHtml(uri: string, play: boolean, loop: boolean): string {
  const safe = uri.replace(/"/g, '&quot;');
  const autoplay = play ? 'autoplay' : '';
  const loop_attr = loop ? 'loop' : '';
  return (
    `<!DOCTYPE html><html><head>` +
    `<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />` +
    `<style>` +
    `html,body{margin:0;padding:0;width:100%;height:100%;background:#000;overflow:hidden}` +
    `video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}` +
    `</style></head><body>` +
    `<video id="v" src="${safe}" ${autoplay} muted ${loop_attr} playsinline webkit-playsinline></video>` +
    `<script>` +
    `(function(){var v=document.getElementById('v');` +
    `function go(){try{window.ReactNativeWebView.postMessage('ready')}catch(e){}}` +
    `if(!v){go();return;}` +
    `v.addEventListener('playing',go,{once:true});` +
    `v.addEventListener('canplay',go,{once:true});` +
    `setTimeout(go,2500);` +
    `})();` +
    `</script>` +
    `</body></html>`
  );
}

function toActive(media: BannerHeroMedia, use_fallback: boolean): ActiveMedia | null {
  if (!use_fallback) {
    return { kind: media.kind, uri: media.uri, youtube_id: media.youtube_id };
  }
  if (!media.fallback_uri || !media.fallback_kind) {
    return null;
  }
  return {
    kind: media.fallback_kind,
    uri: media.fallback_uri,
    youtube_id: null,
  };
}

function resolvePosterUri(media: BannerHeroMedia): string | null {
  if (media.kind === 'image') {
    return media.uri;
  }
  if (media.fallback_kind === 'image' && media.fallback_uri) {
    return media.fallback_uri;
  }
  return null;
}

function NativeFileVideo({
  uri,
  loop,
  paused,
  onError,
  onFirstFrame,
}: {
  uri: string;
  loop: boolean;
  paused: boolean;
  onError: () => void;
  onFirstFrame: () => void;
}) {
  const Video = require('react-native-video').default as React.ComponentType<{
    source: { uri: string };
    style: object;
    resizeMode: string;
    muted: boolean;
    repeat: boolean;
    paused: boolean;
    playInBackground: boolean;
    playWhenInactive: boolean;
    ignoreSilentSwitch: string;
    controls: boolean;
    shutterColor: string;
    onError: () => void;
    onLoad?: () => void;
    onReadyForDisplay?: () => void;
  }>;
  return (
    <Video
      source={{ uri }}
      style={StyleSheet.absoluteFillObject}
      resizeMode="cover"
      muted
      repeat={loop}
      paused={paused}
      playInBackground={false}
      playWhenInactive={false}
      ignoreSilentSwitch="obey"
      controls={false}
      shutterColor="transparent"
      onError={onError}
      onReadyForDisplay={onFirstFrame}
      onLoad={onFirstFrame}
    />
  );
}

export function MainScreenClassicBannerMedia({
  media,
  width,
  height,
  style,
  loop = true,
  onReady,
}: MainScreenClassicBannerMediaProps) {
  const is_focused = useIsFocused();
  const web_ref = useRef<WebView>(null);
  const [use_fallback, setUseFallback] = useState(false);
  /** Video/YouTube has produced a visible frame — drop the still cover. */
  const [playback_frame_ready, set_playback_frame_ready] = useState(false);
  const ready_sent_ref = useRef(false);
  const native_video_ok = useMemo(() => isNativeVideoAvailable(), []);
  const active = useMemo(() => toActive(media, use_fallback), [media, use_fallback]);
  const poster_uri = useMemo(() => resolvePosterUri(media), [media]);

  const notify_ready = useCallback(() => {
    if (ready_sent_ref.current) {
      return;
    }
    ready_sent_ref.current = true;
    afterPaint(() => {
      onReady?.();
    });
  }, [onReady]);

  const on_playback_frame = useCallback(() => {
    set_playback_frame_ready(true);
    if (!poster_uri) {
      notify_ready();
    }
  }, [notify_ready, poster_uri]);

  const on_primary_failed = useCallback(() => {
    setUseFallback(prev => {
      if (prev) {
        return prev;
      }
      return Boolean(media.fallback_uri && media.fallback_kind);
    });
  }, [media.fallback_kind, media.fallback_uri]);

  useEffect(() => {
    setUseFallback(false);
    ready_sent_ref.current = false;
    set_playback_frame_ready(false);
  }, [media.uri, media.fallback_uri, media.kind]);

  useEffect(() => {
    if (active?.kind === 'youtube' && !active.youtube_id) {
      on_primary_failed();
    }
  }, [active, on_primary_failed]);

  // Pause/resume WebView HTML5 video without remounting (keeps decoded frame).
  useEffect(() => {
    if (active?.kind !== 'video' || native_video_ok) {
      return;
    }
    const js = is_focused
      ? `var v=document.getElementById('v');if(v){v.play().catch(function(){})};true;`
      : `var v=document.getElementById('v');if(v){v.pause()};true;`;
    web_ref.current?.injectJavaScript?.(js);
  }, [active?.kind, is_focused, native_video_ok]);

  const on_web_message = useCallback(
    (event: WebViewMessageEvent) => {
      if (event.nativeEvent.data === 'ready') {
        on_playback_frame();
      }
    },
    [on_playback_frame],
  );

  if (active == null) {
    return null;
  }

  const shell_style = [StyleSheet.absoluteFillObject, { width, height }, style];
  const show_poster = Boolean(poster_uri) && !playback_frame_ready;
  /** Stable key — never bump on focus or the hero reloads after back-nav. */
  const media_key = active.uri;

  if (active.kind === 'image') {
    return (
      <View style={[shell_style, styles.hero_chrome]} pointerEvents="none">
        <Image
          key={media_key}
          source={{ uri: active.uri }}
          style={StyleSheet.absoluteFillObject}
          resizeMode="cover"
          fadeDuration={0}
          onLoad={notify_ready}
          onLoadEnd={notify_ready}
          onError={() => {
            on_primary_failed();
            notify_ready();
          }}
        />
      </View>
    );
  }

  return (
    <View style={[shell_style, styles.hero_chrome]} pointerEvents="none">
      {active.kind === 'video' ? (
        native_video_ok ? (
          <NativeFileVideo
            key={media_key}
            uri={active.uri}
            loop={loop}
            paused={!is_focused}
            onError={on_primary_failed}
            onFirstFrame={on_playback_frame}
          />
        ) : (
          <WebView
            ref={web_ref}
            key={media_key}
            source={{ html: fileVideoEmbedHtml(active.uri, true, loop) }}
            style={StyleSheet.absoluteFillObject}
            scrollEnabled={false}
            bounces={false}
            mediaPlaybackRequiresUserAction={false}
            allowsInlineMediaPlayback
            javaScriptEnabled
            domStorageEnabled
            originWhitelist={['*']}
            onHttpError={on_primary_failed}
            onError={on_primary_failed}
            onMessage={on_web_message}
          />
        )
      ) : active.youtube_id ? (
        <WebView
          key={media_key}
          source={{ html: youtubeEmbedHtml(active.youtube_id, loop) }}
          style={StyleSheet.absoluteFillObject}
          scrollEnabled={false}
          bounces={false}
          mediaPlaybackRequiresUserAction={false}
          allowsInlineMediaPlayback
          javaScriptEnabled
          domStorageEnabled
          originWhitelist={['*']}
          onHttpError={on_primary_failed}
          onError={on_primary_failed}
          onMessage={on_web_message}
        />
      ) : null}

      {show_poster && poster_uri ? (
        <Image
          key={poster_uri}
          source={{ uri: poster_uri }}
          style={StyleSheet.absoluteFillObject}
          resizeMode="cover"
          fadeDuration={0}
          onLoad={notify_ready}
          onLoadEnd={notify_ready}
          onError={notify_ready}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  hero_chrome: {
    backgroundColor: '#000000',
  },
});
